<template>
  <div class="space-y-[10px] mb-4">
    <label
      v-if="!!label"
      :for="name"
      class="text-primary-black text-base font-[500]"
      >{{ label }}</label
    >
    <div>
      <div
        :class="[
          'inline-block border-[1px] border-border-divider rounded-2xl p-4 relative space-x-4 whitespace-nowrap max-h-[58px]',
          `after:content-[''] after:border-[0px] after:absolute after:inset-[-1px] after:rounded-[15px] after:border-primary-tangerine after:pointer-events-none`,
          `after:transition-all after:ease-in-out`,
          `after:hover:border-[3px]`,
          { 'after:!border-[3px]': focus },
          {
            'after:!border-error-red after:!border-[3px] !bg-error-red-10 fill-error-red':
              !!errorMessage || props.error
          },
          { 'bg-gray-10': !!inputValue },
          { 'bg-white': !inputValue && props.light },
          { flex: props.full },
          { '!bg-gray-10': disabled },
          { 'flex-col !w-full': props.widthFull }
        ]"
        @click="() => inputRef?.focus()"
      >
        <div
          v-if="(!!props.icon || !!props.phoneSelector) && !props.trailingIcon"
          :class="[
            'inline-block align-middle border-r-[1px] border-border-divider pr-4',
            { 'flex items-center': props.full },
            { 'border-error-red': !!errorMessage || props.error }
          ]"
        >
          <div v-if="!props.phoneSelector && props.icon && !props.trailingIcon">
            <Icon :name="props.icon" />
          </div>
          <div v-if="props.phoneSelector" @click.stop="">
            <FormPhoneSelector
              :error="!!errorMessage || props.error"
              :disabled="disabled"
              @change="handleSelectPhoneCountry"
            />
          </div>
        </div>
        <input
          :id="name"
          ref="inputRef"
          :disabled="disabled"
          :class="[
            'text-black font-normal placeholder:text-border-divider bg-transparent outline-none w-full md:w-full truncate appearance-none',
            {
              '!text-error-red placeholder:!text-error-red':
                !!errorMessage || props.error
            },
            { 'w-[157px]': !props.full },
            { 'flex-1': props.full },
            {
              [`[&:not(:placeholder-shown)]:font-['password']`]:
                props.type == 'password' && !isShowPassword
            },
            { 'text-center': props.textCenter }
          ]"
          :name="name"
          :type="isShowPassword ? 'text' : type"
          :value="getProcessedInputValue"
          :placeholder="placeholder"
          :maxlength="props.maxLength"
          @input="
            (e: Event) => {
              handleChange(e)
              emits('change', (e.target as HTMLInputElement).value)
            }
          "
          @keypress="onlyNumbers"
          @blur="handleOnBlur"
          @focus="focus = true"
          @mouseenter="hover = true"
          @mouseleave="hover = false"
        />
        <div
          v-if="type === 'password' && !disabled"
          class="cursor-pointer"
          @click="isShowPassword = !isShowPassword"
        >
          <Icon :name="isShowPassword ? 'eye' : 'eye-closed'" />
        </div>
        <slot />
        <div v-if="!props.phoneSelector && props.icon && props.trailingIcon">
          <Icon :name="props.icon" />
        </div>
        <div
          v-if="props.type !== 'password' && props.showClearBtn"
          :class="[
            'inline-block invisible align-middle cursor-pointer',
            { '!visible': inputValue || (inputValue && (focus || hover)) },
            { 'flex items-center': props.full }
          ]"
          @click="
            () =>
              (inputValue || (inputValue && (focus || hover))) && setValue('')
          "
        >
          <Icon name="x-circle" />
        </div>
      </div>
    </div>
    <p
      v-if="!!errorMessage || props.error || !!successMessage"
      v-show="errorMessage || meta.valid"
      :class="[
        'text-primary-nimbus flex space-x-1',
        { '!text-error-red fill-error-red': !!errorMessage || props.error }
      ]"
    >
      <Icon
        v-if="!!errorMessage || props.error"
        name="circle-exclamation"
        class="flex items-start"
      />
      {{ props.error ? props.errorMessage : errorMessage || successMessage }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { toRef } from 'vue'
import { useField, useSetFieldValue } from 'vee-validate'
import type { CaseclubIconsId } from '~/public/fonts/CaseClubFontIcons/caseclub-icons'

interface InputProps {
  type?:
    | 'button'
    | 'checkbox'
    | 'color'
    | 'date'
    | 'datetime-local'
    | 'email'
    | 'file'
    | 'hidden'
    | 'image'
    | 'month'
    | 'number'
    | 'password'
    | 'radio'
    | 'range'
    | 'reset'
    | 'search'
    | 'submit'
    | 'tel'
    | 'text'
    | 'time'
    | 'url'
    | 'week'
  value?: string
  name: string
  label?: string
  successMessage?: string
  placeholder?: string
  icon?: CaseclubIconsId
  full?: boolean
  widthFull?: boolean
  showClearBtn?: boolean
  phoneSelector?: boolean
  light?: boolean
  trailingIcon?: boolean
  disabled?: boolean
  textCenter?: boolean
  maxLength?: number
  error?: boolean
  errorMessage?: string
  onlyNumber?: boolean
  fullper?: boolean
}
const onlyNumbers = (event: KeyboardEvent) => {
  if (!/^\d$/.test(event.key) && props.onlyNumber) {
    event.preventDefault()
  }
}

const props = defineProps<InputProps>()
const emits = defineEmits<{
  (e: 'change', value: string): void
}>()

const isShowPassword = ref(false)

const name = toRef(props, 'name')
const {
  value: inputValue,
  errorMessage,
  handleBlur,
  handleChange,
  setValue,
  meta
} = useField(name, undefined, {
  initialValue: props.value
})

const setFieldValue = useSetFieldValue(name.value + '_country_code')

const inputRef = ref<HTMLInputElement | null>(null)
const hover = ref(false)
const focus = ref(false)

const handleSelectPhoneCountry = (value: string) => {
  setFieldValue(value)
}
const handleOnBlur = (focusEvent: FocusEvent) => {
  handleBlur(focusEvent)
  focus.value = false
}
const getProcessedInputValue = computed(() => {
  let processedValue = inputValue.value
  if (props.fullper) {
    processedValue = processedValue.replace(
      /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F700}-\u{1F77F}\u{1F780}-\u{1F7FF}\u{1F800}-\u{1F8FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{2300}-\u{23FF}\u{2B50}\u{23F0}\u{23F3}\u{24C2}\u{1F004}-\u{1F0CF}\u{2B06}\u{2194}\u{2195}\u{23F0}\u{23F3}]+/gu,
      ''
    )
  }
  return processedValue
})

watch(
  () => props.value,
  (nextValue) => {
    if (nextValue) setValue(nextValue)
  }
)
</script>
